<template>
  <div class="mundos-page">
    <div class="mundos-box">
      <div class="mundos-header">
        <h1 class="mundos-title">Mundos</h1>
        <p class="mundos-subtitle">Lista de mundos e jogadores online ao longo do tempo</p>
      </div>
      <div class="mundos-body">
        <div v-if="loadingList" class="mundos-loading">
          <div class="mundos-spinner"></div>
          <p>Carregando mundos...</p>
        </div>
        <template v-else-if="worlds.length">
          <div class="mundos-cards-row">
            <div class="mundos-card mundos-card--gain">
              <h4 class="mundos-card-title">Quem mais upou</h4>
              <div v-if="loadingTopChanges" class="mundos-card-loading">Carregando...</div>
              <template v-else-if="topGain">
                <p class="mundos-card-player">{{ topGain.playerName }}</p>
                <p class="mundos-card-world">{{ topGain.worldName }}</p>
                <p class="mundos-card-levels">
                  {{ topGain.previousLevel }} → {{ topGain.currentLevel }}
                  <span class="mundos-card-gain">+{{ topGain.gain }}</span>
                </p>
                <p v-if="topGain.vocation" class="mundos-card-vocation">{{ topGain.vocation }}</p>
              </template>
              <p v-else class="mundos-card-empty">Sem dados de ganho entre as últimas coletas.</p>
            </div>
            <div class="mundos-card mundos-card--loss">
              <h4 class="mundos-card-title">Quem mais perdeu level</h4>
              <div v-if="loadingTopChanges" class="mundos-card-loading">Carregando...</div>
              <template v-else-if="topLoss">
                <p class="mundos-card-player">{{ topLoss.playerName }}</p>
                <p class="mundos-card-world">{{ topLoss.worldName }}</p>
                <p class="mundos-card-levels">
                  {{ topLoss.previousLevel }} → {{ topLoss.currentLevel }}
                  <span class="mundos-card-loss">-{{ topLoss.loss }}</span>
                </p>
                <p v-if="topLoss.vocation" class="mundos-card-vocation">{{ topLoss.vocation }}</p>
              </template>
              <p v-else class="mundos-card-empty">Sem dados de perda entre as últimas coletas.</p>
            </div>
          </div>
          <p v-if="scrapedAt" class="mundos-meta">Última atualização: {{ formatDate(scrapedAt) }}</p>
          <table class="mundos-table">
            <thead>
              <tr>
                <th>World</th>
                <th>Online</th>
                <th>PvP</th>
                <th>Localização</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="w in worlds" :key="w.worldName" class="mundos-row">
                <td class="mundos-cell-name">{{ w.worldName }}</td>
                <td class="mundos-cell-online">{{ w.onlineCount.toLocaleString("pt-BR") }}</td>
                <td>{{ w.pvpType || "—" }}</td>
                <td>{{ w.location || "—" }}</td>
              </tr>
            </tbody>
          </table>

          <div class="mundos-chart-section">
            <h3 class="mundos-chart-title">Jogadores online ao longo do tempo</h3>
            <p class="mundos-chart-hint">Selecione um ou mais mundos para exibir no gráfico.</p>
            <div class="mundos-chart-legend">
              <label
                v-for="worldName of historyWorlds"
                :key="worldName"
                class="mundos-legend-item"
              >
                <input
                  v-model="selectedWorlds"
                  type="checkbox"
                  :value="worldName"
                />
                <span>{{ worldName }}</span>
              </label>
            </div>
            <div v-if="loadingHistory" class="mundos-loading-inline">
              <div class="mundos-spinner"></div>
              <p>Carregando histórico...</p>
            </div>
            <template v-else-if="chartLabels.length">
              <div
                class="mundos-chart-wrap"
                @mousemove="onChartMouseMove"
                @mouseleave="onChartMouseLeave"
              >
                <div :id="chartId" class="mundos-chart-ct ct-chart"></div>
                <div
                  v-show="tooltip.visible"
                  class="mundos-chart-tooltip"
                  :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }"
                >
                  <div class="mundos-tooltip-date">{{ tooltip.date }}</div>
                  <div
                    v-for="(line, i) in tooltip.lines"
                    :key="i"
                    class="mundos-tooltip-line"
                  >{{ line }}</div>
                </div>
              </div>
            </template>
            <div v-else class="mundos-empty-chart">
              <p>Nenhum dado de histórico ainda.</p>
            </div>
          </div>
        </template>
        <div v-else class="mundos-empty">
          <p>Nenhum dado de mundos disponível ainda.</p>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { getWorlds, getWorldsHistory, getWorldsTopLevelChanges } from "@/api.js";

export default {
  data() {
    return {
      loadingList: true,
      loadingHistory: false,
      loadingTopChanges: false,
      scrapedAt: null,
      worlds: [],
      topGain: null,
      topLoss: null,
      chartId: "mundos-line-chart",
      historyLabels: [],
      historyWorlds: [],
      historySeries: [],
      selectedWorlds: [],
      chartInstance: null,
      tooltip: {
        visible: false,
        x: 0,
        y: 0,
        date: "",
        lines: [],
      },
      chartPaddingLeft: 80,
      chartPaddingRight: 50,
    };
  },
  computed: {
    chartLabels() {
      return this.historyLabels.map((d) => this.formatChartLabel(d));
    },
    chartData() {
      if (!this.historyWorlds.length || !this.historySeries.length) return { labels: [], series: [] };
      const indices = this.selectedWorlds.map((w) => this.historyWorlds.indexOf(w)).filter((i) => i >= 0);
      if (indices.length === 0) return { labels: this.chartLabels, series: [] };
      const series = indices.map((wi) => this.historySeries[wi].map((v) => (v == null ? 0 : v)));
      return {
        labels: this.chartLabels,
        series,
      };
    },
  },
  watch: {
    chartData: {
      deep: true,
      handler(data) {
        if (!this.chartInstance || !data.labels.length) return;
        this.chartInstance.update(data);
      },
    },
    selectedWorlds() {
      if (!this.chartInstance || !this.chartData.series.length) return;
      this.chartInstance.update(this.chartData);
    },
  },
  mounted() {
    this.fetchWorlds();
    this.fetchHistory();
    this.fetchTopLevelChanges();
  },
  beforeDestroy() {
    if (this.chartInstance) {
      this.chartInstance.detach();
      this.chartInstance = null;
    }
  },
  methods: {
    onChartMouseMove(e) {
      const wrap = e.currentTarget;
      if (!wrap || !this.chartData.labels.length || !this.chartData.series.length) return;
      const w = wrap.clientWidth - this.chartPaddingLeft - this.chartPaddingRight;
      if (w <= 0) return;
      const contentX = e.offsetX - this.chartPaddingLeft;
      const n = this.chartData.labels.length;
      let idx = Math.round((contentX / w) * (n - 1));
      if (idx < 0) idx = 0;
      if (idx >= n) idx = n - 1;
      const date = this.chartData.labels[idx];
      const lines = this.selectedWorlds.map(
        (name, i) => `${name}: ${(this.chartData.series[i][idx] ?? 0).toLocaleString("pt-BR")} jogadores`
      );
      let tx = e.offsetX + 12;
      const tw = 260;
      if (tx + tw > wrap.clientWidth) tx = e.offsetX - tw - 8;
      if (tx < 8) tx = 8;
      this.tooltip.visible = true;
      this.tooltip.x = tx;
      this.tooltip.y = e.offsetY + 12;
      this.tooltip.date = date;
      this.tooltip.lines = lines;
    },
    onChartMouseLeave() {
      this.tooltip.visible = false;
    },
    formatDate(d) {
      if (!d) return "—";
      const date = typeof d === "string" ? new Date(d) : d;
      return date.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    formatChartLabel(d) {
      const date = typeof d === "string" ? new Date(d) : d;
      return date.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    async fetchWorlds() {
      this.loadingList = true;
      try {
        const data = await getWorlds();
        this.scrapedAt = data.scrapedAt;
        this.worlds = data.worlds || [];
      } catch (err) {
        this.worlds = [];
      } finally {
        this.loadingList = false;
      }
    },
    async fetchTopLevelChanges() {
      this.loadingTopChanges = true;
      try {
        const data = await getWorldsTopLevelChanges();
        this.topGain = data.topGain || null;
        this.topLoss = data.topLoss || null;
      } catch (err) {
        this.topGain = null;
        this.topLoss = null;
      } finally {
        this.loadingTopChanges = false;
      }
    },
    async fetchHistory() {
      this.loadingHistory = true;
      try {
        const data = await getWorldsHistory({ limit: 200 });
        this.historyLabels = data.labels || [];
        this.historyWorlds = data.worlds || [];
        this.historySeries = data.series || [];
        if (this.historyWorlds.length && this.selectedWorlds.length === 0) {
          this.selectedWorlds = ["Auroria", "Belaria"].filter((w) => this.historyWorlds.includes(w));
          if (this.selectedWorlds.length === 0) this.selectedWorlds = [...this.historyWorlds];
        }
      } catch (err) {
        this.historyLabels = [];
        this.historyWorlds = [];
        this.historySeries = [];
      } finally {
        this.loadingHistory = false;
        this.$nextTick(() => this.initChart());
      }
    },
    initChart() {
      const data = this.chartData;
      if (!data.labels.length || !data.series.length) return;
      const labels = [...data.labels];
      const series = data.series.map((s) => [...s]);
      import("chartist").then((Chartist) => {
        const Ct = Chartist.default || Chartist;
        if (this.chartInstance) {
          this.chartInstance.detach();
          this.chartInstance = null;
        }
        this.$nextTick(() => {
          const el = document.getElementById(this.chartId);
          if (!el) return;
          const pad = 50;
          this.chartInstance = new Ct.Line(
            `#${this.chartId}`,
            { labels, series },
            {
              axisX: {
                labelInterpolationFnc: (value, i) => (i % Math.max(1, Math.ceil(labels.length / 8)) === 0 ? value : null),
              },
              axisY: {
                onlyInteger: true,
              },
              low: 0,
              showArea: false,
              showLine: true,
              showPoint: true,
              fullWidth: true,
              chartPadding: { top: pad, right: pad, bottom: pad, left: pad + 30 },
            }
          );
        });
      });
    },
  },
};
</script>
<style scoped lang="scss">
.mundos-page {
  padding: 1rem 0;
}
.mundos-box {
  max-width: 960px;
  margin: 0 auto;
  background: rgba(26, 24, 20, 0.85);
  border: 1px solid rgba(139, 105, 20, 0.45);
  border-radius: 8px;
  padding: 1.5rem;
}
.mundos-header {
  margin-bottom: 1rem;
}
.mundos-title {
  color: #d4af37;
  font-size: 1.5rem;
  margin: 0 0 0.25rem 0;
}
.mundos-subtitle {
  color: #8a7a5c;
  font-size: 0.9rem;
  margin: 0;
}
.mundos-cards-row {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.25rem;
}
.mundos-card {
  flex: 1;
  min-width: 200px;
  background: rgba(20, 18, 14, 0.9);
  border: 1px solid rgba(139, 105, 20, 0.4);
  border-radius: 8px;
  padding: 1rem 1.25rem;
}
.mundos-card--gain {
  border-left: 3px solid #7cb342;
}
.mundos-card--loss {
  border-left: 3px solid #ef5350;
}
.mundos-card-title {
  color: #d4af37;
  font-size: 1rem;
  margin: 0 0 0.5rem 0;
  font-weight: 600;
}
.mundos-card-player {
  color: #e8dcc4;
  font-size: 1.05rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
}
.mundos-card-world {
  color: #8a7a5c;
  font-size: 0.85rem;
  margin: 0 0 0.35rem 0;
}
.mundos-card-levels {
  color: #c9b896;
  font-size: 0.95rem;
  margin: 0 0 0.2rem 0;
}
.mundos-card-gain {
  color: #7cb342;
  font-weight: 600;
}
.mundos-card-loss {
  color: #ef5350;
  font-weight: 600;
}
.mundos-card-vocation {
  color: #8a7a5c;
  font-size: 0.85rem;
  margin: 0;
}
.mundos-card-empty,
.mundos-card-loading {
  color: #8a7a5c;
  font-size: 0.9rem;
  margin: 0;
}
.mundos-meta {
  color: #8a7a5c;
  font-size: 0.85rem;
  margin-bottom: 0.75rem;
}
.mundos-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
  thead th {
    text-align: left;
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid rgba(139, 105, 20, 0.4);
    color: #d4af37;
  }
  tbody td {
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid rgba(139, 105, 20, 0.2);
    color: #c9b896;
  }
}
.mundos-row {
  &:hover {
    background: rgba(212, 175, 55, 0.06);
  }
}
.mundos-cell-name {
  font-weight: 500;
}
.mundos-cell-online {
  text-align: right;
}
.mundos-chart-section {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(139, 105, 20, 0.35);
}
.mundos-chart-title {
  color: #d4af37;
  font-size: 1.2rem;
  margin: 0 0 0.25rem 0;
}
.mundos-chart-hint {
  color: #8a7a5c;
  font-size: 0.85rem;
  margin: 0 0 0.75rem 0;
}
.mundos-chart-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  margin-bottom: 1rem;
}
.mundos-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: #e8dcc4;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  input {
    cursor: pointer;
    width: 16px;
    height: 16px;
    accent-color: #d4af37;
  }
}
.mundos-chart-wrap {
  position: relative;
  height: 420px;
  width: 100%;
  cursor: crosshair;
}
.mundos-chart-tooltip {
  position: absolute;
  pointer-events: none;
  z-index: 10;
  background: rgba(20, 18, 14, 0.96);
  border: 1px solid rgba(212, 175, 55, 0.5);
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  min-width: 140px;
  max-width: 260px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
}
.mundos-tooltip-date {
  color: #d4af37;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 0.35rem;
  padding-bottom: 0.25rem;
  border-bottom: 1px solid rgba(212, 175, 55, 0.3);
}
.mundos-tooltip-line {
  color: #c9b896;
  font-size: 0.85rem;
  line-height: 1.4;
}
.mundos-chart-ct {
  height: 420px;
  width: 100%;
  position: relative;
  padding-bottom: 0;
}
.mundos-chart-ct::before {
  display: none;
}
.mundos-chart-ct ::v-deep .ct-chart-line .ct-series {
  stroke: currentColor;
}
.mundos-chart-ct ::v-deep svg {
  overflow: visible;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.mundos-chart-ct ::v-deep .ct-grid {
  stroke: rgba(212, 175, 55, 0.35);
  stroke-width: 1px;
}
.mundos-chart-ct ::v-deep .ct-label {
  fill: #c9b896;
  color: #c9b896;
  font-size: 13px;
  font-weight: 500;
}
.mundos-chart-ct ::v-deep .ct-line {
  stroke-width: 2.5px;
}
.mundos-chart-ct ::v-deep .ct-point {
  stroke-width: 3px;
}
.mundos-chart-ct ::v-deep .ct-series-a { stroke: #d4af37; }
.mundos-chart-ct ::v-deep .ct-series-b { stroke: #7cb342; }
.mundos-chart-ct ::v-deep .ct-series-c { stroke: #42a5f5; }
.mundos-chart-ct ::v-deep .ct-series-d { stroke: #ab47bc; }
.mundos-chart-ct ::v-deep .ct-series-e { stroke: #ef5350; }
.mundos-chart-ct ::v-deep .ct-series-f { stroke: #26a69a; }
.mundos-chart-ct ::v-deep .ct-series-g { stroke: #ffa726; }
.mundos-chart-ct ::v-deep .ct-series-h { stroke: #66bb6a; }
.mundos-chart-ct ::v-deep .ct-series-i { stroke: #5c6bc0; }
.mundos-chart-ct ::v-deep .ct-series-j { stroke: #ec407a; }
.mundos-chart-ct ::v-deep .ct-series-k { stroke: #8d6e63; }
.mundos-chart-ct ::v-deep .ct-series-l { stroke: #26c6da; }
.mundos-chart-ct ::v-deep .ct-series-m { stroke: #ffee58; }
.mundos-chart-ct ::v-deep .ct-series-n { stroke: #b39ddb; }
.mundos-chart-ct ::v-deep .ct-series-o { stroke: #a5d6a7; }
.mundos-loading,
.mundos-loading-inline {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #8a7a5c;
  p { margin: 0; }
}
.mundos-loading-inline {
  margin-top: 1rem;
}
.mundos-spinner {
  width: 24px;
  height: 24px;
  border: 2px solid rgba(212, 175, 55, 0.3);
  border-top-color: #d4af37;
  border-radius: 50%;
  animation: mundos-spin 0.8s linear infinite;
}
@keyframes mundos-spin {
  to { transform: rotate(360deg); }
}
.mundos-empty,
.mundos-empty-chart {
  color: #8a7a5c;
  text-align: center;
  padding: 2rem;
  p { margin: 0; }
}
.mundos-empty-chart {
  padding: 1.5rem;
}
</style>
